import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { login as apiLogin, verifyMfa as apiVerifyMfa, getMe, logout as apiLogout, registerPatient } from '../api/auth.js'
import {
  getAccessToken, setAccessToken,
  setRefreshToken, clearTokens,
  getStoredUser, setStoredUser,
} from '../utils/storage.js'
import { firstError } from '../utils/format.js'

const AuthCtx = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => getStoredUser())
  const [loading, setLoading] = useState(() => Boolean(getAccessToken()) && !getStoredUser())
  const [error, setError] = useState(null)

  useEffect(() => {
    // If we have a token but no cached user (e.g. after a page refresh
    // before /me finished), re-fetch the profile.
    if (!getAccessToken() || getStoredUser()) { setLoading(false); return }
    let alive = true
    getMe()
      .then((u) => { if (alive) { setUser(u); setStoredUser(u) } })
      .catch(() => { if (alive) clearTokens() })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  // Accept either a username (Django default) or an email — SimpleJWT is
  // configured against the default User model which authenticates by
  // username, so we forward whatever the user typed as `username`.
  async function signIn(usernameOrEmail, password) {
    setError(null)
    try {
      const data = await apiLogin(usernameOrEmail, password)
      // MFA challenge: password was correct but a second factor is required.
      // Hand the short-lived mfa_token back to the caller (Login) to drive
      // the code-entry step — do NOT store any tokens yet.
      if (data && data.mfa_required) {
        return {
          mfaRequired: true,
          mfaToken: data.mfa_token,
          method: data.method || 'email',
          methods: data.methods || ['email'],
          emailSent: !!data.email_sent,
        }
      }
      const access = data.access || data.access_token
      const refresh = data.refresh || data.refresh_token
      if (access) setAccessToken(access)
      if (refresh) setRefreshToken(refresh)
      // SimpleJWT's token view does not embed the user — always follow up
      // with /api/auth/me/ to hydrate role + profile.
      const u = data.user || (await getMe())
      setUser(u); setStoredUser(u)
      return { mfaRequired: false, user: u }
    } catch (e) {
      setError(firstError(e, 'Login failed'))
      throw e
    }
  }

  /**
   * Login step 2 — exchange the mfa_token + 6-digit emailed code (or a
   * recovery code) for the real JWT pair, then hydrate the user.
   */
  async function verifyMfa(mfaToken, code) {
    setError(null)
    try {
      const data = await apiVerifyMfa(mfaToken, code)
      const access = data.access || data.access_token
      const refresh = data.refresh || data.refresh_token
      if (access) setAccessToken(access)
      if (refresh) setRefreshToken(refresh)
      const u = await getMe()
      setUser(u); setStoredUser(u)
      return u
    } catch (e) {
      setError(firstError(e, 'Verification failed'))
      throw e
    }
  }

  async function signOut() {
    try { await apiLogout() } catch { /* no-op: backend has no logout route */ }
    clearTokens()
    setUser(null)
  }

  /**
   * Patient self-registration. Creates the account and signs the patient
   * in atomically (the backend returns both JWTs + a hydrated user), then
   * calls /api/auth/me/ to pick up the full role-aware profile payload.
   */
  async function signUpPatient(payload) {
    setError(null)
    try {
      const data = await registerPatient(payload)
      const access = data.access
      const refresh = data.refresh
      if (access) setAccessToken(access)
      if (refresh) setRefreshToken(refresh)
      // Backend's hydrated user is enough, but follow up with /me for the
      // full profile vector (adds roles + profile.id etc.).
      const u = await getMe()
      setUser(u); setStoredUser(u)
      return u
    } catch (e) {
      setError(firstError(e, 'Registration failed'))
      throw e
    }
  }

  /**
   * Generic "sign in with JWTs we already obtained some other way" — e.g.
   * the clinician-invitation accept flow returns { access, refresh } with
   * the 201 response, no username/password round-trip needed.
   */
  async function signInWithTokens({ access, refresh }) {
    setError(null)
    if (access) setAccessToken(access)
    if (refresh) setRefreshToken(refresh)
    const u = await getMe()
    setUser(u); setStoredUser(u)
    return u
  }

  /**
   * Re-fetch /api/auth/me/ and refresh the in-memory user state.
   * Useful after a profile edit or a late org self-enrollment so the
   * onboarding checklist (and any other piece keying off `user.*`)
   * picks up the change without a full page reload.
   */
  async function refreshUser() {
    try {
      const u = await getMe()
      setUser(u); setStoredUser(u)
      return u
    } catch (e) {
      // Silently swallow — caller may already have rendered an error.
      return null
    }
  }

  const value = useMemo(
    () => ({ user, loading, error, signIn, verifyMfa, signUpPatient, signInWithTokens, signOut, refreshUser }),
    [user, loading, error],
  )
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>
}

export const useAuth = () => useContext(AuthCtx)
